/**
 * AudioPlayer — universal audio playback component.
 *
 * Replaces both VoiceMemo.js (posts) and VoiceMemoPlayer.jsx (DMs) with one
 * component that handles voice memos AND uploaded audio files (mp3, m4a, etc.).
 *
 * Features:
 *   - Waveform-style progress visualisation
 *   - Draggable scrub bar with time tooltip (via ScrubBar)
 *   - Playback speed control (1x / 1.5x / 2x)
 *   - Adapts colors for own-message vs received-message in DMs
 *   - Accepts both URL-based audio (Supabase) and legacy base64
 *   - Shows filename for uploaded audio files
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';
import ScrubBar from '@/components/shared/ScrubBar';

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function AudioPlayer({
  src,
  duration: initialDuration = 0,
  mimeType,
  filename,
  isOwnMessage = false,
  compact = false,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [errorRetried, setErrorRetried] = useState(false);
  const audioRef = useRef(null);

  // Sync isPlaying with actual audio element state
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => { a.removeEventListener('play', onPlay); a.removeEventListener('pause', onPause); };
  }, []);

  // Detect URL vs base64
  const isUrl = src && (src.startsWith('http://') || src.startsWith('https://'));
  const primaryMime = mimeType || 'audio/mp4';
  const audioSrc = isUrl ? src : `data:${primaryMime};base64,${src}`;

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const handleDurationAvailable = useCallback(() => {
    if (audioRef.current && isFinite(audioRef.current.duration) && audioRef.current.duration > 0) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, []);

  const handleSeek = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const cycleRate = useCallback(() => {
    const rates = [1, 1.5, 2];
    setPlaybackRate(prev => rates[(rates.indexOf(prev) + 1) % rates.length]);
  }, []);

  // Color scheme
  const own = isOwnMessage;
  const colors = {
    bg: own ? 'bg-blue-400/30' : 'bg-slate-100 dark:bg-[#3a3d41]',
    playBtn: own ? 'bg-white text-blue-500 hover:bg-blue-50' : 'bg-blue-500 text-white hover:bg-blue-600',
    text: own ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400',
    speedBtn: own ? 'bg-blue-400/50 text-white hover:bg-blue-400/70' : 'bg-slate-200 dark:bg-[#3e4145] text-slate-600 dark:text-slate-300 hover:bg-slate-300',
    scrubTrackBg: own ? 'bg-blue-300/30' : 'bg-slate-200 dark:bg-[#3e4145]',
    scrubFill: own ? 'bg-white/80' : 'bg-blue-500',
    scrubThumb: own ? 'bg-white' : 'bg-white',
  };

  const playSize = compact ? 'w-9 h-9' : 'w-10 h-10';
  const iconSize = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <div
      className={`rounded-xl p-3 ${colors.bg} ${compact ? 'min-w-[180px] max-w-[240px]' : 'min-w-[200px] max-w-[320px]'}`}
      data-testid="audio-player"
    >
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={handleDurationAvailable}
        onDurationChange={handleDurationAvailable}
        onEnded={handleEnded}
        onError={() => {
          // Legacy base64 format fallback (try once)
          if (!isUrl && audioRef.current && !errorRetried) {
            setErrorRetried(true);
            const fallbackMime = primaryMime.includes('webm') ? 'audio/mp4' : 'audio/webm';
            audioRef.current.src = `data:${fallbackMime};base64,${src}`;
          }
        }}
        preload="metadata"
      />

      <div className="flex items-center gap-3">
        {/* Play / Pause */}
        <button
          onClick={togglePlay}
          className={`${playSize} ${colors.playBtn} rounded-full flex items-center justify-center shadow-sm transition-all hover:scale-105 flex-shrink-0`}
          data-testid="audio-player-play-btn"
        >
          {isPlaying ? <Pause className={iconSize} /> : <Play className={`${iconSize} ml-0.5`} />}
        </button>

        {/* Scrub + controls */}
        <div className="flex-1 min-w-0">
          <ScrubBar
            progress={progress}
            duration={duration}
            currentTime={currentTime}
            onSeek={handleSeek}
            colors={{ trackBg: colors.scrubTrackBg, fillBg: colors.scrubFill, thumbBg: colors.scrubThumb }}
          />

          {/* Time + speed */}
          <div className="flex items-center justify-between -mt-1">
            <span className={`text-[10px] font-medium ${colors.text}`}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              onClick={cycleRate}
              className={`text-[10px] font-semibold ${colors.speedBtn} px-1.5 py-0.5 rounded transition-colors`}
              data-testid="audio-player-speed-btn"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>

      {/* Filename for uploaded audio files */}
      {filename && (
        <p className={`text-[10px] mt-1.5 truncate ${colors.text}`} title={filename}>
          {filename}
        </p>
      )}
    </div>
  );
}
